import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import PdfViewDrawer from "../../../../../../../../components/PdfViewDrawer";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import Text from "../../../../../../../../components/Text";
import ChartTableWrapper from "../../../../../../../../components/app/provider/patient/add-new-patient/ChartTableWrapper";
import useCurrentUser from "../../../../../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { usePatientPortalLabResultsHealthRecord } from "../../../../../../../../queries/patient-chart/result/use-get-patient-portal-lab-results";
import { NonNullableDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getFormattedId,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../utils/utils";
import { ABNORMAL_FLAG_OPTIONS_MAPPER } from "./UpsertResultNew";

const OrderReportsHealthV2 = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams();
  const [currentUser] = useCurrentUser();
  const patientUuid = patientId || currentUser?.uuid;
  const previewResultDisc = useDisclosure();

  const { data: labResults } = usePatientPortalLabResultsHealthRecord({
    patientUuid: patientUuid ?? "",
    pagination,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "uuid",
        headerName: "Order ID",
        flex: 0.3,
        width: 100,
        renderCell: ({ row }: any) => (
          <Text isTabletext>{getFormattedId(row?.uuid)}</Text>
        ),
      },
      {
        field: "orderType",
        headerName: "Order Type",
        flex: 0.4,
        width: 140,
        renderCell: ({ value }: any) =>
          value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
      },
      {
        field: "recordedDate",
        headerName: "Ordered On",
        flex: 0.5,
        width: 160,
        renderCell: ({ row }: any) => NonNullableDate(row?.recordedDate),
      },
      {
        field: "patientOrder",
        headerName: "Order Title",
        flex: 0.6,
        width: 200,
        renderCell: ({ row, value }: any) => {
          const orderName = getValueFromObj(value) || "-";
          const labDoc = row?.labDocument;
          if (labDoc) {
            return (
              <Text
                isTabletext
                sx={{ cursor: "pointer" }}
                color="primary.main"
                onClick={() => previewResultDisc.open(labDoc)}
              >
                {orderName}
              </Text>
            );
          }
          return <Text isTabletext>{orderName}</Text>;
        },
      },
      {
        field: "resultValue",
        headerName: "Result Value",
        flex: 0.4,
        width: 120,
        renderCell: ({ value }: any) => value ?? "-",
      },
      {
        field: "abnormalFlag",
        headerName: "Abnormal Flag",
        flex: 0.5,
        width: 160,
        renderCell: ({ value }: any) =>
          ABNORMAL_FLAG_OPTIONS_MAPPER[value] || "-",
      },
      {
        field: "createdByName",
        headerName: "Reviewer",
        flex: 0.5,
        width: 160,
        renderCell: ({ value }: any) => <RenderWithTooltip value={value} />,
      },
    ],
    ["recordedDate"]
  );

  return (
    <>
      <PdfViewDrawer
        w="65vw"
        file={previewResultDisc?.data}
        disclosureHost={previewResultDisc}
        title="Order Result"
      />

      <ChartTableWrapper>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={labResults?.content ?? []}
          rowCount={labResults?.page?.totalElements ?? 10}
          columns={COLUMNS}
          rowHeight={50}
          sx={{ marginTop: "40px" }}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
        />
      </ChartTableWrapper>
    </>
  );
};

export default OrderReportsHealthV2;
